import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import axios from 'axios';
import { setClarityUser } from '../components/Analytics';
import { supabase, getUserProfile } from '../lib/supabase';

const AuthContext = createContext();

// Setup axios interceptor at module level (outside React lifecycle)
// This ensures it's only set up once and not affected by React Strict Mode
let interceptorSetup = false;

// Guard against double auth initialization (React Strict Mode / double renders)
let authInitStarted = false;

const setupAxiosInterceptor = () => {
  if (interceptorSetup) {
    return;
  }

  axios.interceptors.request.use(
    async (config) => {
      try {
        // Always get fresh session from Supabase (not from React state)
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (currentSession?.access_token) {
          config.headers.Authorization = `Bearer ${currentSession.access_token}`;
        } else {
          delete config.headers.Authorization;
        }
      } catch (error) {
        console.error('Error getting session for request:', error);
        delete config.headers.Authorization;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  interceptorSetup = true;
};

// Set up interceptor immediately when module loads
setupAxiosInterceptor();

/**
 * Retry profile fetch with exponential backoff
 * @param {string} userId - User ID
 * @param {string} token - Access token
 * @param {number} maxRetries - Maximum retry attempts
 * @returns {Promise<object|null>} Profile data or null
 */
const fetchProfileWithRetry = async (userId, token, maxRetries = 3) => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`[Auth] Profile fetch attempt ${attempt}/${maxRetries}`);
      const profile = await getUserProfile(userId, token);
      if (profile) {
        console.log(`[Auth] Profile fetch succeeded on attempt ${attempt}`);
        return profile;
      }
      // Wait before retry (exponential backoff: 1s, 2s, 3s)
      if (attempt < maxRetries) {
        const delay = 1000 * attempt;
        console.log(`[Auth] Waiting ${delay}ms before retry...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    } catch (error) {
      console.error(`[Auth] Profile fetch attempt ${attempt} failed:`, error);
      if (attempt < maxRetries) {
        const delay = 1000 * attempt;
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  console.warn(`[Auth] All ${maxRetries} profile fetch attempts failed`);
  return null;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  // Sync Supabase token to localStorage['access_token'] for backwards compatibility
  // Many components use localStorage.getItem('access_token') for API calls
  const syncTokenToLocalStorage = (accessToken) => {
    if (accessToken) {
      localStorage.setItem('access_token', accessToken);
    } else {
      localStorage.removeItem('access_token');
    }
  };

  // Initialize auth state and listen for changes
  useEffect(() => {
    // Clear any corrupted auth tokens on first load
    const clearCorruptedTokens = () => {
      // Only clear old-style tokens, NOT access_token (we use it for backwards compatibility)
      const legacyKeys = ['token', 'user'];
      // Also check for corrupted Supabase internal storage
      const supabaseKey = 'sb-lifhcxxnxrqolzhfsldf-auth-token';

      legacyKeys.forEach(key => {
        if (localStorage.getItem(key)) {
          localStorage.removeItem(key);
        }
      });

      // Check if Supabase token is corrupted (malformed JWT)
      const supabaseData = localStorage.getItem(supabaseKey);
      if (supabaseData) {
        try {
          const parsed = JSON.parse(supabaseData);
          const token = parsed?.access_token;
          if (token) {
            // Valid JWT should have exactly 3 parts separated by dots
            const parts = token.split('.');
            if (parts.length !== 3) {
              localStorage.removeItem(supabaseKey);
            }
          }
        } catch (e) {
          localStorage.removeItem(supabaseKey);
        }
      }
    };

    // Get initial session
    const initializeAuth = async () => {
      // Guard against double initialization (React Strict Mode)
      if (authInitStarted) {
        return;
      }
      authInitStarted = true;

      // Clear corrupted tokens first
      clearCorruptedTokens();

      // Safety timeout to prevent infinite loading (reduced from 15s to 3s for better UX)
      const timeoutId = setTimeout(() => {
        setLoading(false);
      }, 3000);

      try {
        // Parallelize auth calls for faster initialization
        const [sessionResult, userResult] = await Promise.all([
          supabase.auth.getSession(),
          supabase.auth.getUser()
        ]);

        const { data: { session: currentSession }, error } = sessionResult;
        const { data: { user: authUser }, error: userError } = userResult;

        if (error) {
          console.error('❌ Supabase getSession error:', error);
          // If there's a session error, try to sign out to clear corrupted state
          try {
            await supabase.auth.signOut();
          } catch (signOutError) {
            // Silent fail - cleanup attempt
          }
        } else {
          if (currentSession) {
            // Verify the session is valid by checking the user
            if (userError || !authUser) {
              console.error('❌ Session validation failed:', userError);
              // Session is invalid, sign out
              await supabase.auth.signOut();
            } else {
                setSession(currentSession);
                syncTokenToLocalStorage(currentSession?.access_token);

                // Get user profile from database with retry mechanism
                try {
                  // Use retry mechanism to handle timing issues
                  const profile = await fetchProfileWithRetry(currentSession.user.id, currentSession.access_token, 3);

                  if (profile) {
                    setUser(profile);

                    // Check if user needs onboarding
                    if (profile.onboarding_completed === false) {
                      setNeedsOnboarding(true);
                    }

                    // Identify user in Microsoft Clarity
                    setClarityUser(profile.id || profile.email, {
                      subscription_plan: profile.subscription_plan,
                      is_admin: profile.is_admin,
                      session_type: 'existing'
                    });
                  } else {
                    // Fallback to basic user data - mark as incomplete
                    console.warn('[Auth] Profile fetch failed after all retries, using fallback defaults');
                    setUser({
                      id: currentSession.user.id,
                      email: currentSession.user.email,
                      is_admin: false,
                      subscription_plan: 'free',
                      _profileFetchFailed: true
                    });
                  }
                } catch (profileError) {
                  // Fallback with explicit defaults to prevent undefined behavior
                  console.error('[Auth] Profile fetch exception:', profileError);
                  setUser({
                    id: currentSession.user.id,
                    email: currentSession.user.email,
                    is_admin: false,
                    subscription_plan: 'free',
                    _profileFetchFailed: true
                  });
                }
            }
          }
        }
      } catch (error) {
        console.error('❌ Auth initialization error:', error);
        // Clear any potentially corrupted auth state
        try {
          await supabase.auth.signOut();
        } catch (e) {}
      } finally {
        clearTimeout(timeoutId);
        setLoading(false);
      }
    };

    // Track if initial auth is complete to avoid race conditions
    let initialAuthComplete = false;

    // Listen for auth state changes (after initial auth)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      // Skip ALL events during initial auth to avoid race condition
      // The initializeAuth function handles the initial session loading
      if (!initialAuthComplete) {
        return;
      }

      setSession(newSession);
      syncTokenToLocalStorage(newSession?.access_token);

      if (newSession?.user) {
        // Get user profile from database with retry mechanism
        try {
          const profile = await fetchProfileWithRetry(newSession.user.id, newSession.access_token, 3);

          if (profile) {
            setUser(profile);

            // Check if user needs onboarding
            if (profile.onboarding_completed === false) {
              setNeedsOnboarding(true);
            }
          } else {
            console.warn('[Auth] onAuthStateChange: Profile fetch failed, using fallback');
            setUser({
              id: newSession.user.id,
              email: newSession.user.email,
              is_admin: false,
              subscription_plan: 'free',
              _profileFetchFailed: true
            });
          }
        } catch (profileError) {
          console.error('[Auth] onAuthStateChange: Profile fetch exception:', profileError);
          setUser({
            id: newSession.user.id,
            email: newSession.user.email,
            is_admin: false,
            subscription_plan: 'free',
            _profileFetchFailed: true
          });
        }
      } else {
        setUser(null);
        setNeedsOnboarding(false);
      }
    });

    // Run initial auth and mark as complete when done
    initializeAuth().then(() => {
      initialAuthComplete = true;
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  const login = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        console.error('❌ Supabase login error:', error);
        return {
          success: false,
          error: error.message || 'Login failed'
        };
      }

      // Get user profile with retry mechanism
      if (data.user) {
        try {
          const profile = await fetchProfileWithRetry(data.user.id, data.session?.access_token, 3);

          if (profile) {
            setUser(profile);

            // Check if user needs onboarding
            if (profile.onboarding_completed === false) {
              setNeedsOnboarding(true);
            }

            // Identify user in Microsoft Clarity
            setClarityUser(profile.id || profile.email, {
              subscription_plan: profile.subscription_plan,
              is_admin: profile.is_admin,
              login_method: 'email'
            });
          } else {
            console.warn('[Auth] login: Profile fetch failed, using fallback');
            setUser({
              id: data.user.id,
              email: data.user.email,
              is_admin: false,
              subscription_plan: 'free',
              _profileFetchFailed: true
            });
          }
        } catch (profileError) {
          // Continue with basic user data instead of blocking login
          console.error('[Auth] login: Profile fetch exception:', profileError);
          setUser({
            id: data.user.id,
            email: data.user.email,
            is_admin: false,
            subscription_plan: 'free',
            _profileFetchFailed: true
          });
        }
      }

      setSession(data.session);
      syncTokenToLocalStorage(data.session?.access_token);
      return { success: true };
    } catch (error) {
      console.error('❌ Login error:', error);
      return {
        success: false,
        error: error.message || 'Login failed'
      };
    }
  };

  const register = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`
        }
      });

      if (error) {
        console.error('❌ Supabase registration error:', error);
        return {
          success: false,
          error: error.message || 'Registration failed'
        };
      }

      // If auto-confirm is enabled, user will be logged in
      if (data.session) {
        setSession(data.session);
        syncTokenToLocalStorage(data.session?.access_token);

        // Get user profile (created by database trigger)
        if (data.user) {
          // Wait a moment for the trigger to create the profile
          await new Promise(resolve => setTimeout(resolve, 500));

          try {
            const profile = await fetchProfileWithRetry(data.user.id, data.session?.access_token, 3);

            if (profile) {
              setUser(profile);

              // New users always need onboarding
              setNeedsOnboarding(true);

              // Identify user in Microsoft Clarity
              setClarityUser(profile.id || profile.email, {
                subscription_plan: profile.subscription_plan,
                is_admin: profile.is_admin,
                registration_method: 'email'
              });
            } else {
              console.warn('[Auth] register: Profile fetch failed, using fallback');
              setUser({
                id: data.user.id,
                email: data.user.email,
                is_admin: false,
                subscription_plan: 'free',
                _profileFetchFailed: true
              });
              // New users still need onboarding even with failed profile fetch
              setNeedsOnboarding(true);
            }
          } catch (profileError) {
            console.error('[Auth] register: Profile fetch exception:', profileError);
            setUser({
              id: data.user.id,
              email: data.user.email,
              is_admin: false,
              subscription_plan: 'free',
              _profileFetchFailed: true
            });
            setNeedsOnboarding(true);
          }
        }
      }

      return {
        success: true,
        confirmEmail: !data.session // If no session, email confirmation is required
      };
    } catch (error) {
      console.error('❌ Registration error:', error);
      return {
        success: false,
        error: error.message || 'Registration failed'
      };
    }
  };

  const logout = async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      // Silent fail - logout will complete anyway
    }

    setSession(null);
    setUser(null);
    setNeedsOnboarding(false);
    syncTokenToLocalStorage(null); // Clear access_token from localStorage
  };

  const completeOnboarding = () => {
    setNeedsOnboarding(false);
  };

  const refreshUser = async () => {
    if (session?.user) {
      try {
        const profile = await fetchProfileWithRetry(session.user.id, session.access_token, 3);

        if (profile) {
          setUser(profile);
          return true;
        }
      } catch (error) {
        console.error('[Auth] refreshUser failed:', error);
      }
    }
    return false;
  };

  /**
   * Retry loading user profile - useful when _profileFetchFailed is true
   * @returns {Promise<boolean>} True if profile was successfully loaded
   */
  const refreshProfile = async () => {
    if (!session?.access_token || !user?.id) {
      console.warn('[Auth] refreshProfile: No session or user ID available');
      return false;
    }

    try {
      console.log('[Auth] refreshProfile: Attempting to reload profile...');
      const profile = await fetchProfileWithRetry(user.id, session.access_token, 3);

      if (profile) {
        console.log('[Auth] refreshProfile: Success - is_admin:', profile.is_admin, 'plan:', profile.subscription_plan);
        setUser(profile);
        return true;
      }

      console.warn('[Auth] refreshProfile: Failed to load profile');
      return false;
    } catch (error) {
      console.error('[Auth] refreshProfile: Exception:', error);
      return false;
    }
  };

  const resetPassword = async (email) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const updatePassword = async (newPassword) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const signInWithGoogle = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent'
          }
        }
      });

      if (error) {
        console.error('❌ Google OAuth error:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('❌ Google OAuth error:', error);
      return { success: false, error: error.message };
    }
  };

  const checkFeatureAccess = (feature) => {
    // Client-side feature checks for premium features
    const premiumFeatures = ['reports', 'csv_export', 'email_reminders', 'unlimited_subscriptions', 'dark_mode', 'payment_calculator'];

    if (premiumFeatures.includes(feature)) {
      return user?.subscription_plan === 'premium' || user?.is_admin;
    }

    // Default to true for basic features
    return true;
  };

  const checkFeatureAccessAsync = async (feature) => {
    try {
      const response = await axios.get(`${API_BASE}/api/features/check/${feature}`);
      return response.data.has_access;
    } catch (error) {
      // Silent fail - feature check failed, deny access by default
      return false;
    }
  };

  const getSubscriptionLimits = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/subscription-limits`);
      return response.data;
    } catch (error) {
      // Silent fail - limits check failed
      return null;
    }
  };

  const value = {
    user,
    session,
    token: session?.access_token, // For backwards compatibility
    loading,
    needsOnboarding,
    completeOnboarding,
    login,
    register,
    logout,
    refreshUser,
    refreshProfile, // New: retry profile fetch when _profileFetchFailed is true
    resetPassword,
    updatePassword,
    signInWithGoogle,
    checkFeatureAccess,
    checkFeatureAccessAsync,
    getSubscriptionLimits,
    isAuthenticated: !!user,
    isPremium: user?.subscription_plan === 'premium',
    isAdmin: user?.is_admin || false,
    profileFetchFailed: user?._profileFetchFailed || false // Expose flag for UI
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
